import { useState } from "react";
import { extractErrorMessage, useAdminAPI, adminApi as api } from "../../util/api";
import { camelizeKeys, snakifyKeys } from "../../util/helpers";
import { SystemConfigLayout } from "../components/SystemConfigLayout";
import { TextInput } from "../../components/inputs/final_form/TextInput";
import { Switch } from "../../components/inputs/final_form/Switch";
import { Form, Field } from "react-final-form";
import { Card, CardBody, Col, Row, Spinner, FormGroup, CardFooter } from "reactstrap";
import { composeValidators, required, numericInRange, wholeNumber } from "../../util/validators";
import { LoadingButton } from "../../components/LoadingButton";
import { DetailedErrorAlert } from "../../components/DetailedErrorAlert";
import { CircleX, CircleCheck, Info } from "lucide-react";
import { Fieldset } from "../../components/inputs/final_form/Fieldset";

export const AssignmentAutomationSettingsDashboard = () => {
  const [updateError, setUpdateError] = useState();

  const { data, error, isLoading } = useAdminAPI(`/admin/case_assignment_automation_configs/get_instance`);

  const updateInstance = async (values) => {
    try {
      const params = snakifyKeys(values);

      await api.patch(`/admin/case_assignment_automation_configs/update_instance`, { ...params });
    } catch (e) {
      setUpdateError(extractErrorMessage(e));
    }
  };

  return (
    <SystemConfigLayout title="Assignment Automation Settings">
      <Form onSubmit={updateInstance} initialValues={camelizeKeys(data)}>
        {({ values, handleSubmit, submitting, submitSucceeded, invalid }) => {
          return (
            <Col>
              {error && <DetailedErrorAlert className="mb-2" error={error} />}
              {updateError && <DetailedErrorAlert className="mb-2" error={updateError} />}

              {isLoading && (
                <div className="d-flex flex-row justify-content-center align-items-center w-100 h-100">
                  <Spinner color="secondary">Loading...</Spinner>
                </div>
              )}
              {data && (
                <>
                  <Row>
                    <Col>
                      <Card className="border-0 bg-info bg-opacity-25 mb-3">
                        <CardBody className="pb-0">
                          <FormGroup>
                            <Field
                              component={TextInput}
                              name="triggerBackfillPercentage"
                              type="number"
                              min="0"
                              label={<strong>Trigger Case Backfill:</strong>}
                              validate={composeValidators(required, wholeNumber, numericInRange(0, 100))}
                              placeholder="between 0 and 100..."
                              className="w-25"
                            />
                            <span>
                              <Info className="me-2" />% of minimal target threshold
                            </span>
                          </FormGroup>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Card className="border-0 bg-warning bg-opacity-25 mb-3">
                        <CardBody className="pb-0">
                          <FormGroup>
                            <Field
                              component={TextInput}
                              name="refillPercentage"
                              type="number"
                              min="0"
                              max="100"
                              label={<strong>Refill Percentage:</strong>}
                              validate={composeValidators(required, wholeNumber, numericInRange(0, 100), (value) =>
                                parseInt(value, 10) >= parseInt(values.triggerBackfillPercentage, 10)
                                  ? undefined
                                  : "Refill percentage should be greater than trigger percentage"
                              )}
                              placeholder="between 0 and 100..."
                              className="w-25"
                            />
                            <span>
                              <Info className="me-2" />% of target to fill up to
                            </span>
                          </FormGroup>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      <Card className="border-0 mb-3">
                        <CardBody className="pb-0">
                          <Fieldset legend="Override Target Number (Global)">
                            <FormGroup>
                              <Field
                                type="checkbox"
                                component={Switch}
                                required
                                label="Enable Global Target Override"
                                name="targetNumberOverrideEnabled"
                                id="targetNumberOverrideEnabled"
                              />

                              {values.targetNumberOverrideEnabled && (
                                <Field
                                  component={TextInput}
                                  name="targetNumberOverride"
                                  type="number"
                                  min="0"
                                  label="Override Target Number"
                                  validate={composeValidators(required, wholeNumber, numericInRange(0, 120))}
                                  required={values.targetNumberOverrideEnabled}
                                  className="w-25"
                                />
                              )}
                            </FormGroup>
                          </Fieldset>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>

                  <div className="d-flex flex-row">
                    <LoadingButton color="primary" onClick={handleSubmit} loading={submitting} disabled={invalid}>
                      Save
                    </LoadingButton>

                    {submitSucceeded && !updateError && (
                      <div className="ms-1 mt-1">
                        <CircleCheck color="green" className="me-1" /> Updated!
                      </div>
                    )}

                    {submitSucceeded && updateError && (
                      <div className="ms-1 mt-1">
                        <CircleX color="red" className="me-1" /> Failed to Update!
                      </div>
                    )}
                  </div>
                </>
              )}
            </Col>
          );
        }}
      </Form>
    </SystemConfigLayout>
  );
};
